import { Box, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RenderHtml } from 'components/RenderHtml';
import OrganizationChip from 'components/OrganizationChip';
import { showErrorAction } from 'containers/Notification/actions';
import LocationObject from 'entities/locationObject/model';
import { GooderaBookOpportunityPayload, useImportOpportunityMutation } from 'generated/graphql';
import { BackButton } from 'retired/BackButtonLayout/BackButton';
import { mediumGray } from 'utils/color-styles';
import { renderAddress } from 'utils/renderAddress';

import { BookGooderaOpportunityModal } from '../OpportunityCard/BookGooderaOpportunityModal';
import { ImportOpportunityButton } from '../OpportunityCard/ImportOpportunityButton';
import { Opportunity } from '../types';

import { OpportunityDate } from './OpportunityDate';
import { OpportunityImage } from './OpportunityImage';

export const OpportunityPreview = ({
  opportunity,
  setOpportunityPreview,
}: {
  opportunity: Opportunity;
  setOpportunityPreview: (opportunity: null | Opportunity) => void;
}) => {
  const dispatch = useDispatch();
  const [importOpportunity] = useImportOpportunityMutation({
    onError: () => {
      dispatch(showErrorAction('An error occurred, please try again.'));
    },
  });
  const history = useHistory();
  const [eventBookingCommenced, setEventBookingCommenced] = useState(false);

  const importOpportunityHandler = async (
    gooderaBookOpportunityPayload?: GooderaBookOpportunityPayload
  ) => {
    const importResult = await importOpportunity({
      variables: {
        provider: opportunity.provider,
        providerId: opportunity.providerId,
        gooderaBookOpportunityPayload,
      },
    });
    const importedOpportunityId = importResult.data?.importOpportunity?.id;
    if (importedOpportunityId) {
      history.push(`/deeds/projects/edit/${importedOpportunityId}`);
    }
  };

  return (
    <>
      <Box mb={10}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex">
            <BackButton
              onClick={() => {
                setOpportunityPreview(null);
              }}
              style={{ marginRight: 16 }}
            />
            <Typography fontSize={32} style={{ marginRight: 16 }}>
              {opportunity.name}
            </Typography>
          </Box>

          <ImportOpportunityButton opportunity={opportunity} />
        </Box>
        <Grid container spacing={4}>
          <Grid item md={8}>
            <OpportunityImage opportunity={opportunity} />
            {opportunity.description && (
              <>
                <Typography mt={2} fontSize={20}>
                  Description
                </Typography>
                <Box fontSize={14}>
                  <RenderHtml source={{ html: opportunity.description }} />
                </Box>
              </>
            )}
            {opportunity.mustKnows && (
              <>
                <Typography mt={2} fontSize={20}>
                  Must Knows
                </Typography>
                <Box fontSize={14}>
                  <RenderHtml source={{ html: opportunity.mustKnows }} />
                </Box>
              </>
            )}
          </Grid>
          <Grid item md={4}>
            <Box
              bgcolor="white"
              borderRadius="12px"
              border="1px solid"
              borderColor={mediumGray}
              padding={3}
            >
              {opportunity.nonprofit && (
                <Box>
                  <Typography mb={1}>Organized by:</Typography>
                  <OrganizationChip organization={opportunity.nonprofit} />
                </Box>
              )}

              <OpportunityDate opportunity={opportunity} />

              {opportunity.contact && (
                <Box py={2} borderTop={`1px solid ${mediumGray}`}>
                  <Typography>
                    Point of Contact:{' '}
                    <a href={`mailto:${opportunity.contact.email}`} target="_blank">
                      {opportunity.contact.name}
                    </a>{' '}
                  </Typography>
                </Box>
              )}
              {(opportunity.virtual || opportunity.location?.city) && (
                <Box py={2} borderTop={`1px solid ${mediumGray}`}>
                  <Typography>
                    Location:{' '}
                    {opportunity.virtual
                      ? 'Virtual'
                      : renderAddress(opportunity.location as LocationObject)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      {eventBookingCommenced && (
        <BookGooderaOpportunityModal
          opportunity={opportunity}
          onClose={() => setEventBookingCommenced(false)}
          importOpportunity={importOpportunityHandler}
        />
      )}
    </>
  );
};
